/**
 * v7.22 — Mercado Pago Card Brick.
 * v7.36 — Stabilization (campos não limpam mais) + Google Pay/Wallet disabled.
 *
 * Renderiza o Brick `cardPayment` do MP SDK v2. O cartão é tokenizado no
 * navegador (PCI-compliant) e o token é enviado pro backend que cria o
 * pagamento via `/api/integrations/mp/create-card-payment`.
 *
 * Props:
 *   - amount: valor do pedido (number)
 *   - description: descrição do pedido
 *   - customerEmail: email do payer (gerado pelo telefone se ausente)
 *   - onApproved({ id, status, lastFourDigits, installments }): callback ao aprovar
 *   - onError(error): callback em recusa/erro
 *
 * Carrega o SDK on-demand via <script>. Lê a public key do backend
 * (`loadServerConfig` → `cfg.mpPublicKey`).
 *
 * IMPORTANTE (v7.36):
 *  - Callbacks ficam em ref. O useEffect de boot só depende de
 *    `amount/description/customerEmail`. Isso evita remontar o brick a cada
 *    render do parent (que era a causa real dos campos limparem durante
 *    a digitação).
 *  - Google Pay e Wallet Purchase são desabilitados por config + CSS para
 *    eliminar o autofill que sobrescreve o número do cartão.
 */
import { useEffect, useRef, useState } from 'react';
import { loadServerConfig } from '@/lib/serverConfig';
import { Loader2, CreditCard, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SDK_URL = 'https://sdk.mercadopago.com/js/v2';
const CONTAINER_ID = 'kaven-card-brick';

let _sdkPromise = null;
function loadSdk() {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if (window.MercadoPago) return Promise.resolve(window.MercadoPago);
  if (_sdkPromise) return _sdkPromise;
  _sdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SDK_URL}"]`);
    const handle = (s) => {
      s.addEventListener('load', () => resolve(window.MercadoPago));
      s.addEventListener('error', () => reject(new Error('Falha ao carregar SDK Mercado Pago')));
    };
    if (existing) {
      if (window.MercadoPago) resolve(window.MercadoPago);
      else handle(existing);
      return;
    }
    const s = document.createElement('script');
    s.src = SDK_URL;
    s.async = true;
    handle(s);
    document.head.appendChild(s);
  });
  return _sdkPromise;
}

export default function CardCheckoutBrick({ amount, description, customerEmail, onApproved, onError }) {
  const containerRef = useRef(null);
  const brickControllerRef = useRef(null);
  const [phase, setPhase] = useState('loading'); // loading | ready | submitting | error
  const [errorMsg, setErrorMsg] = useState('');
  const [publicKey, setPublicKey] = useState('');

  // v7.36 — Callbacks em ref pra evitar remountar o brick a cada render do
  // parent (que era a causa real dos campos limparem durante a digitação:
  // inline arrow functions criam novas refs em cada render → useEffect
  // re-dispara → brick destruído e recriado → input wiped).
  const onApprovedRef = useRef(onApproved);
  const onErrorRef = useRef(onError);
  useEffect(() => { onApprovedRef.current = onApproved; }, [onApproved]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // Cleanup ao desmontar — destrói o brick para evitar leak.
  useEffect(() => {
    return () => {
      try { brickControllerRef.current?.unmount?.(); } catch {}
      brickControllerRef.current = null;
    };
  }, []);

  // Boot: pega public key + SDK + monta brick.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setPhase('loading');
        setErrorMsg('');
        const body = await loadServerConfig();
        const pk = body?.config?.mpPublicKey || '';
        if (!pk) throw new Error('MP_PUBLIC_KEY não configurada no servidor');
        if (cancelled) return;
        setPublicKey(pk);

        const MP = await loadSdk();
        if (cancelled) return;
        const mp = new MP(pk, { locale: 'pt-BR' });
        const bricksBuilder = mp.bricks();

        const settings = {
          initialization: {
            amount: Number(amount),
            payer: { email: customerEmail || '' },
          },
          customization: {
            visual: {
              style: { theme: 'default' },
              // v7.36 — Esconde botões de wallet (Google Pay / Mercado Pago)
              // que apareciam acima do form e disparavam autofill ao
              // serem inicializados, sobrescrevendo o número digitado.
              hideFormTitle: false,
              hidePaymentButton: false,
            },
            paymentMethods: {
              maxInstallments: 6,
              // v7.36 — Defesa em profundidade: chaves redundantes que
              // versões diferentes do SDK v2 do MP usam pra desabilitar
              // Google Pay e walletPurchase. Nenhuma dispara erro se a
              // versão ignorar a chave.
              googlePay: false,
              walletPurchase: false,
              types: {
                excluded: ['googlepay', 'wallet_purchase'],
              },
            },
          },
          callbacks: {
            onReady: () => {
              if (!cancelled) setPhase('ready');
            },
            onSubmit: async ({ formData }) => {
              if (cancelled) return;
              setPhase('submitting');
              setErrorMsg('');
              try {
                const resp = await fetch(`${API_URL}/api/integrations/mp/create-card-payment`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    token: formData.token,
                    issuer_id: formData.issuer_id,
                    payment_method_id: formData.payment_method_id,
                    installments: formData.installments,
                    transaction_amount: Number(amount),
                    description: description || 'Pedido Kaven',
                    payer: formData.payer,
                  }),
                });
                if (!resp.ok) {
                  const txt = await resp.text().catch(() => '');
                  throw new Error(`HTTP ${resp.status}: ${txt.slice(0, 200)}`);
                }
                const data = await resp.json();
                if (data.status === 'approved') {
                  // v7.36 — Lê o callback da ref (sempre atualizado, sem remount)
                  onApprovedRef.current?.(data);
                } else {
                  const detail = data.statusDetail || data.status || 'recusado';
                  setErrorMsg(`Pagamento ${detail}. Tente outro cartão.`);
                  setPhase('error');
                  onErrorRef.current?.(new Error(detail));
                }
              } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[CARD BRICK] submit falhou:', err);
                setErrorMsg(err?.message || 'Falha ao processar pagamento');
                setPhase('error');
                onErrorRef.current?.(err);
              }
            },
            onError: (err) => {
              // eslint-disable-next-line no-console
              console.warn('[CARD BRICK] erro SDK:', err);
              if (!cancelled) {
                setErrorMsg(err?.message || 'Erro no formulário de cartão');
                setPhase('error');
              }
            },
          },
        };

        const controller = await bricksBuilder.create('cardPayment', CONTAINER_ID, settings);
        if (cancelled) {
          try { controller?.unmount?.(); } catch {}
          return;
        }
        brickControllerRef.current = controller;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[CARD BRICK] boot falhou:', err);
        if (!cancelled) {
          setErrorMsg(err?.message || 'Falha ao iniciar checkout de cartão');
          setPhase('error');
        }
      }
    })();
    return () => { cancelled = true; };
    // v7.36 — DEPS REDUZIDAS: callbacks NÃO entram na dep list (vivem em ref).
    // Isso impede o brick de ser remountado a cada render do parent — que
    // era a causa real dos campos do cartão limparem durante a digitação.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, description, customerEmail]);

  return (
    <div data-testid="card-checkout-brick" className="space-y-3">
      {/* v7.36 — CSS defensivo: esconde qualquer botão de Google Pay /
          wallet que o SDK MP ainda tente renderizar dentro do iframe ou
          como sibling. Usa attribute-selectors abrangentes para pegar
          variações de id/class que o MP usa em diferentes versões. */}
      <style>{`
        #${CONTAINER_ID} [data-element="googlepay-button"],
        #${CONTAINER_ID} [data-testid*="googlepay"],
        #${CONTAINER_ID} [id*="googlePay"],
        #${CONTAINER_ID} [id*="google-pay"],
        #${CONTAINER_ID} [class*="googlepay"],
        #${CONTAINER_ID} [class*="google-pay"],
        #${CONTAINER_ID} [class*="walletPurchase"],
        #${CONTAINER_ID} [data-element="walletPurchase"],
        #${CONTAINER_ID} .mp-button-googlepay,
        #${CONTAINER_ID} .gpay-button,
        #${CONTAINER_ID} .gpay-card-info-container {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `}</style>
      <div className="flex items-center gap-2">
        <CreditCard size={16} className="text-cyan-600" />
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Pagamento com cartão</p>
      </div>

      {phase === 'loading' && (
        <div data-testid="card-brick-loading" className="flex items-center gap-2 text-slate-500 text-sm py-6 justify-center">
          <Loader2 size={16} className="animate-spin" /> Carregando checkout seguro...
        </div>
      )}

      {phase === 'submitting' && (
        <div data-testid="card-brick-submitting" className="flex items-center gap-2 text-cyan-700 text-sm py-3 justify-center font-semibold">
          <Loader2 size={16} className="animate-spin" /> Processando pagamento...
        </div>
      )}

      {/* Container do Brick — sempre presente; SDK manipula o DOM dentro */}
      <div id={CONTAINER_ID} ref={containerRef} className={phase === 'error' ? 'opacity-50' : ''} />

      {phase === 'error' && (
        <div data-testid="card-brick-error" className="flex items-start gap-2 px-3 py-2.5 rounded-lg border bg-red-50 border-red-200 text-red-800 text-xs font-semibold">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span className="leading-relaxed">{errorMsg || 'Erro desconhecido'}</span>
        </div>
      )}

      {publicKey && (
        <p data-testid="card-brick-environment" className="text-[10px] text-slate-400 text-center">
          🔒 Tokenização segura via Mercado Pago Bricks · public key {publicKey.slice(0, 12)}…
        </p>
      )}
    </div>
  );
}
